/**
 * resolveJourneyVariant - the single decision point for which journey surface
 * the dashboard renders at the QuestCard seam (F2b D1; codex R1/I1).
 *
 * Pure and fail-safe. It answers ONE question: does this cohort render the
 * credential Act-1 spine, or the untouched stock QuestCard?
 *
 *   - Strict `credential_bearing === true` (coerced via isCredentialBearing,
 *     so an absent/degraded/non-boolean flag can only ever fall through) AND a
 *     valid current capsule id  ->  `credential`.
 *   - Everything else (non-credential, ambiguous, or missing capsule while the
 *     challenge is still loading)  ->  `stock`.
 *
 * DELIBERATELY NOT act-aware and NOT clearance-aware (codex I1): a
 * confirmed-credential cohort whose clearance is still loading or has failed
 * must stay on the credential spine (which owns its own loading/error state) -
 * it must NEVER fall back to the stock QuestCard, because the legacy Mission
 * step would then expose its generic redacted rendering. So clearance status is
 * intentionally not an input here; the variant is fixed by challenge identity +
 * capsule validity alone, and the spine handles the rest.
 *
 * The stock branch renders the EXISTING QuestCard with byte-identical props -
 * this resolver's only job is to keep non-credential cohorts on the exact path
 * they are on today (proven by the golden snapshot + parity suites).
 */
import { coerceCapsuleId, sameCapsuleId } from "../../utils/capsuleId";
import { isCredentialBearing } from "./resolveTrainingEntry";

export const JOURNEY_VARIANT = Object.freeze({
  CREDENTIAL: "credential",
  STOCK: "stock",
});

// The capsule-id contract moved to src/utils/capsuleId.js when the store began
// reading it too (a reducer must not import a page module). Re-exported here so
// the modules that already resolve it through this one keep working, and so
// there is still exactly one implementation.
export { coerceCapsuleId };

/**
 * @param {object} args
 * @param {object|null} args.challenge - the active challenge (ChallengeResource)
 * @param {number|string|null} args.capsuleId - the current level's capsule id
 * @returns {"credential" | "stock"}
 */
export function resolveJourneyVariant({ challenge, capsuleId } = {}) {
  if (!isCredentialBearing(challenge)) return JOURNEY_VARIANT.STOCK;
  if (coerceCapsuleId(capsuleId) != null) return JOURNEY_VARIANT.CREDENTIAL;
  // Confirmed credential but no valid capsule yet (challenge still hydrating):
  // stay on stock until a capsule resolves. A credential cohort ALWAYS has a
  // capsule once program data lands, so this is a transient loading window, not
  // a path a real credential student is ever stranded on.
  return JOURNEY_VARIANT.STOCK;
}

/**
 * shouldHideGamifiedChrome - credential cohorts hide XP / prize / rank /
 * leaderboard chrome in the dashboard header (diegetic-lexicon contract: a
 * credential is cleared, not scored). Strict boolean coercion so an absent,
 * numeric, or string flag can never accidentally hide chrome for a stock
 * cohort (parity tests cover absent/false/numeric/string).
 *
 * @param {object|null} challenge
 * @returns {boolean}
 */
export function shouldHideGamifiedChrome(challenge) {
  return isCredentialBearing(challenge);
}

/**
 * resolveMissionHandoff - pure decision for the `?openMission={capsuleId}`
 * handoff (F2b D3). Kept pure so the branch matrix is unit-tested without a full
 * Dashboard render.
 *
 * Returns one of:
 *   { action: "open", capsuleId } - valid, credential, current capsule: the
 *       caller refetches program data then opens the credential MissionPage.
 *   { action: "scrub" } - malformed / non-credential / not the
 *       current capsule: strip the param, do nothing (a stale link never opens
 *       a mission; the backend is the real gate).
 *
 * @param {object} args
 * @param {string|null} args.param - the raw ?openMission query value
 * @param {object|null} args.challenge - active challenge (ChallengeResource)
 * @param {number|string|null} args.currentCapsuleId - the current level capsule
 * @returns {{action:"open", capsuleId:number} | {action:"scrub"}}
 */
export function resolveMissionHandoff({
  param,
  challenge,
  currentCapsuleId,
} = {}) {
  const capsuleId = coerceCapsuleId(param);
  if (
    capsuleId == null ||
    !isCredentialBearing(challenge) ||
    coerceCapsuleId(currentCapsuleId) !== capsuleId
  ) {
    return { action: "scrub" };
  }
  return { action: "open", capsuleId };
}

/**
 * deriveCredentialEffectiveStatus - the EFFECTIVE credential journey status
 * (F2b C1 fix / code-review). This is the PRODUCER the reviewer required: it
 * derives the step a credential student is really on WITHOUT depending on
 * capsule_user.read_objective (which the credential mission path correctly never
 * writes, so the stock Dashboard cascade would otherwise pin credential students
 * at status 1 forever).
 *
 * It leaves the stock cascade UNTOUCHED (the redux `status` is still produced by
 * the unchanged v4 ladder). Instead, for a credential cohort whose mission has
 * OPENED, it treats gate 1 (read_objective) as satisfied and runs the SAME
 * downstream gate order (module completion, reflection, plan/task, work, report,
 * deliverable) on real data - returning the step the credential spine + the
 * status-gated Dashboard surfaces should reflect. This mirrors the v4 ladder at
 * Dashboard.jsx (v4 gates 2..7); keep them in sync.
 *
 * Before the mission opens (Act 1), the redux status is accurate (0 = kickoff
 * pending, 1 = at the mission gate), so we pass it straight through.
 *
 * @param {object} args
 * @param {number} args.reduxStatus - the stock redux status (Act-1 passthrough)
 * @param {boolean} args.missionOpened - clearance act_state.mission_opened
 * @param {boolean} args.moduleCompleted - last module is_completed
 * @param {boolean} args.reflectionPresent - last module reflection exists
 * @param {boolean} args.taskCreated - mission.isTaskCreated
 * @param {boolean} args.allTasksComplete - every own quest is complete
 * @param {boolean} args.reportSubmitted - milestone_report_submitted
 * @param {boolean} args.deliverablesSubmitted - deliverables_submitted
 * @returns {number} the effective status (0..7)
 */
export function deriveCredentialEffectiveStatus({
  reduxStatus = 1,
  missionOpened = false,
  moduleCompleted = false,
  reflectionPresent = false,
  taskCreated = false,
  allTasksComplete = false,
  reportSubmitted = false,
  deliverablesSubmitted = false,
} = {}) {
  // Act 1 (mission not open): the stock redux status is accurate.
  if (!missionOpened) return reduxStatus;
  // Act 2: gate 1 (read_objective) is treated satisfied; run gates 2..7.
  if (!moduleCompleted) return 2; // concepts
  if (!reflectionPresent) return 3; // bridge
  /**
   * THE PLAN SKIP, MIRRORED FROM THE STOCK LADDER (W4-F2.11 fix batch, Fable
   * MINOR 5).
   *
   * Amir, 2026-09-19: "I was actually okay with skipping the plan step if the
   * deliverable is already submitted. I like that skip." Planning tasks for an
   * output already turned in is hollow work.
   *
   * ON THE STOCK LADDER THAT SKIP IS A WRITE-THROUGH: `deriveJourneyStep`
   * dispatches `SET_TASK_CREATED` when the stamp is in, and the next pass walks
   * past gate 4. That branch returns early for a credential journey, so the
   * credential rail never had it, and before this chip nobody noticed: the
   * squad's stamp crossed the whole LEVEL, so the learner was never held
   * anywhere. Now that the level boundary correctly waits for the learner's own
   * walkthrough, the missing skip is visible as a taskless credential squadmate
   * parked at Plan, planning work for an output that is already in.
   *
   * IT IS A READ HERE AND A WRITE THERE, and that difference is the function's
   * own contract rather than a divergence: this one is pure over the snapshot
   * it is handed and produces no side effects at all, so the same fact that
   * makes the stock ladder write `taskCreated` simply satisfies the gate.
   *
   * ONLY GATE 4, EXACTLY AS ON THE STOCK LADDER. A learner with a task still
   * open is held at Work by gate 5 whether the squad's output is in or not: the
   * stamp says the SQUAD shipped, and it has never said anything about whether
   * this learner finished what they claimed.
   */
  if (!taskCreated && !deliverablesSubmitted) return 4; // plan
  if (!allTasksComplete) return 5; // work
  if (!reportSubmitted) return 6; // huddle / progress report
  if (!deliverablesSubmitted) return 7; // ship / deliverable
  return 7; // fully complete
}

/**
 * isCredentialLevelComplete - the credential mirror of the stock v4 terminal
 * branch (codex P0-2). True only when the mission is open AND every downstream
 * gate is satisfied on real data (module, reflection, plan/task, work, report,
 * deliverable) - i.e. the level's deliverable is submitted. The Dashboard uses
 * this to dispatch the SAME level-advance the stock terminal branch dispatches;
 * the credential v4-gate-1 pin otherwise makes the stock terminal unreachable,
 * so a credential student who submits their final deliverable would never
 * advance to the next capsule.
 *
 * W4-F2.11, ADDENDUM 6 C: AND IT CARRIES THE SAME OWN-WALKTHROUGH GATE THE
 * STOCK RAIL CARRIES.
 *
 * `deliverablesSubmitted` is `capsule_users.deliverables_submitted`, and since
 * Wave 4 B2 the reader floor stamps it on EVERY member of the squad's roster
 * snapshot the instant the output passes (`FloorTransition.php:55-60`). F2.10
 * corrected the stock ladder for that (`deriveJourneyStep`'s `pastOwnShip`) and
 * left this function reading the pre-B2 meaning, so on a credential rail the
 * squad's stamp still crossed a level for a learner who had never been offered
 * Ship: no drawer, no Next, no ending, and the rail on the next level. That is
 * addendum 5 rule 1, which is about the LEVEL boundary and says nothing about
 * which rail the boundary is on.
 *
 * THE SECOND FACT IS THE SAME ONE, AND IT IS AN INPUT FOR THE SAME REASON. This
 * function is pure over the snapshot it is handed; `selectJourneyInputs` and
 * `Dashboard.jsx` both own the read of the learner's own ship-walkthrough
 * ledger, and both must pass it. The default is `false`, so a caller that has
 * not been taught to pass it holds the level rather than crossing it, which is
 * the direction the ruling's own disclosed cost points in: one extra
 * acknowledgement, never a level the learner did not walk to.
 *
 * WHAT A FALSE ANSWER DOES ON EACH SIDE. In `deriveJourneyStep` the credential
 * arm falls through to the Act 2 ladder and
 * `deriveCredentialEffectiveStatus` answers 7, so the learner stands on Ship
 * with the drawer's `Submitted` and its Next, which is exactly where the stock
 * ladder puts them. In `Dashboard.jsx` the terminal effect's decision is
 * NOTHING, so no transition moment opens and no capsule advances until the
 * learner's own ship event lands.
 *
 * @param {object} inputs - deriveCredentialEffectiveStatus's shape, plus the
 *   learner's own ship walkthrough for this capsule
 * @returns {boolean}
 */
export function isCredentialLevelComplete({
  missionOpened = false,
  moduleCompleted = false,
  reflectionPresent = false,
  taskCreated = false,
  allTasksComplete = false,
  reportSubmitted = false,
  deliverablesSubmitted = false,
  shipWalkthroughRecorded = false,
} = {}) {
  /**
   * THE PLAN GATE MIRRORS THE LADDER'S SKIP, AND IT HAS TO (W4-F2.11 fix batch,
   * Fable MINOR 5).
   *
   * `deriveCredentialEffectiveStatus` now walks a taskless squadmate past Plan
   * on the squad's stamp, exactly as the stock ladder does. A boundary that
   * still demanded `taskCreated` would then hold that learner at Ship for ever:
   * the credential rail never writes `mission.isTaskCreated` (its arm of the
   * derivation returns before the stock shortcut), so the flag they were
   * skipped past would be the flag that closed the level to them.
   *
   * IT IS THE SAME RULE THE STOCK RAIL ALREADY APPLIES, spelled rather than
   * implied: there the stamp WRITES the flag, so the stock terminal gate reads
   * a true it was handed by the stamp. Here the stamp satisfies the gate
   * directly. Written out rather than deleted, because "the stamp stands in for
   * the plan" is the rule, and a gate that had simply dropped `taskCreated`
   * would read as an omission.
   */
  const planSatisfied = taskCreated === true || deliverablesSubmitted === true;
  return (
    missionOpened === true &&
    moduleCompleted === true &&
    reflectionPresent === true &&
    planSatisfied &&
    allTasksComplete === true &&
    reportSubmitted === true &&
    deliverablesSubmitted === true &&
    shipWalkthroughRecorded === true
  );
}

/**
 * credentialTerminalDecision. What the credential terminal effect should do
 * when a level's gates close. Pure, so the ordering that used to be implicit in
 * an effect body is now stated once and tested.
 *
 * The bug this replaces (dogfood, cohort 34): the inline version burned its
 * one-shot ref BEFORE reading mission.nextCapsule. The submit thunk merges
 * deliverables_submitted into redux immediately, but nextCapsule only arrives
 * with the mission refetch ~800ms later, so the first run took the "no next
 * capsule" arm, wrongly flagging a NON-final level as the end of the challenge,
 * and burned the ref, so the run that finally had nextCapsule died at the
 * guard. The level stalled until a manual reload reset the ref.
 *
 * The fix is the ordering: `markAdvanced` is an OUTPUT of the decision, so a run
 * without the payload marks nothing and the next run retries.
 *
 * @param {object} args
 * @param {boolean} args.gatesComplete - isCredentialLevelComplete(gateInputs)
 * @param {number|string|null} args.currentCapsuleId - the capsule on screen
 * @param {number|string|null} args.nextCapsuleId - mission.nextCapsule?.id
 * @param {boolean} args.alreadyAdvanced - this capsule already advanced once
 * @param {number|null} args.ordinal - validated 1-based level ordinal, or null
 * @param {number|null} args.totalLevels - validated level count, or null
 * @returns {{showTransition:boolean, advanceTo:(number|null),
 *   setLastLevelFlag:boolean, markAdvanced:boolean}}
 */
export function credentialTerminalDecision({
  gatesComplete = false,
  currentCapsuleId = null,
  nextCapsuleId = null,
  alreadyAdvanced = false,
  ordinal = null,
  totalLevels = null,
} = {}) {
  const NOTHING = {
    showTransition: false,
    advanceTo: null,
    setLastLevelFlag: false,
    markAdvanced: false,
  };
  if (gatesComplete !== true) return NOTHING;
  if (alreadyAdvanced === true) return NOTHING;

  // The destination goes through the capsule-id contract at the boundary, not
  // raw. Two reasons, both real: a nextCapsule id that coerces to nothing
  // ("abc", 0, "") is not a capsule and must never open a transition; and the
  // value returned here becomes the `to` half of the reducer's compare-and-set,
  // so forwarding "502" while the mission holds 502 would produce a moment whose
  // acknowledgement can never apply.
  const advanceTo = coerceCapsuleId(nextCapsuleId);
  if (advanceTo !== null) {
    // Belt and braces with the reducer's own compare-and-set: a payload whose
    // nextCapsule points back at the capsule we are already on is stale, and
    // "advancing" to it would consume the transition for nothing.
    if (sameCapsuleId(advanceTo, currentCapsuleId)) return NOTHING;
    return {
      showTransition: true,
      advanceTo,
      setLastLevelFlag: false,
      markAdvanced: true,
    };
  }

  // No usable next capsule. Either this is genuinely the last level, or the mission
  // refetch has not landed yet, and those two are indistinguishable from this
  // field alone, which is exactly what the old code got wrong. The validated
  // ordinal is what separates them, and it fails closed: without it we neither
  // flag nor mark, so the effect simply retries when more data arrives.
  const isFinalLevel =
    Number.isInteger(ordinal) &&
    Number.isInteger(totalLevels) &&
    ordinal === totalLevels;
  return {
    showTransition: false,
    advanceTo: null,
    setLastLevelFlag: isFinalLevel,
    markAdvanced: false,
  };
}

/**
 * credentialAdvanceOutcome. What the transition moment's dismissal handler
 * should do after dispatching the compare-and-set advance (codex round-2
 * finding 6). The dispatch is synchronous, so the handler reads the store back
 * and asks this rule whether the advance actually landed.
 *
 * If the CAS no-opped (a mission payload changed nextCapsule while the moment
 * was open) the ref must be CLEARED, so the terminal effect can re-derive and
 * show the moment again when a valid payload returns. Acknowledging a
 * celebration must never be the thing that strands a student on a finished
 * level.
 *
 * @param {object} args
 * @param {number|string|null} args.missionCapsuleId - post-dispatch capsule id
 * @param {number|string|null} args.toCapsuleId - the capsule we tried to reach
 * @returns {{applied:boolean, clearAdvancedRef:boolean}}
 */
export function credentialAdvanceOutcome({
  missionCapsuleId = null,
  toCapsuleId = null,
} = {}) {
  const applied = sameCapsuleId(missionCapsuleId, toCapsuleId);
  return { applied, clearAdvancedRef: !applied };
}

export default resolveJourneyVariant;
